import { useEffect, useRef, useState } from "react";
import * as s from "./report.styles";
import styled from "styled-components";
import FarmCompetitive from "./Economy/FarmCompetitive";
import Cost from "./Economy/Cost";
import { ReportFarmEconomic } from "../machine";
import { formatPriceNumber, formatWonToEokMan } from "../function";

const tabList = ["영농 수익 구조", "매출", "비용", "수익률", "손익분기점"];
type tabId = (typeof tabList)[number];
const HEADER_OFFSET = 110;

interface Props {
  scrollContainerRef?: React.RefObject<HTMLElement>;
  data: ReportFarmEconomic;
  crops: number;
  hideInnerTabs?: boolean;
}

function Economy({ scrollContainerRef, data, crops, hideInnerTabs }: Props) {
  const [activeTab, setActiveTab] = useState("영농 수익 구조");
  const [isTotal, setIsTotal] = useState(false);
  const [openRoiUnitChange, setOpenRoiUnitChange] = useState(false);
  const [roiUnit, setRoiUnit] = useState("총 투자");

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const currentRef = dropdownRef.current;

      if (currentRef && !currentRef.contains(target)) {
        setOpenRoiUnitChange(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const revenueStructureRef = useRef<HTMLDivElement>(null); // 영농 수익 구조
  const farmCompetitivenessRef = useRef<HTMLDivElement>(null); // 농장 경쟁력
  const salesRef = useRef<HTMLDivElement>(null); // 매출
  const costRef = useRef<HTMLDivElement>(null); // 비용
  const profitRateRef = useRef<HTMLDivElement>(null); // 수익률
  const breakEvenRef = useRef<HTMLDivElement>(null); // 손익분기점

  const tabToRef: Record<string, React.RefObject<HTMLDivElement>> = {
    "영농 수익 구조": revenueStructureRef,
    "농장 경쟁력": farmCompetitivenessRef,
    매출: salesRef,
    비용: costRef,
    수익률: profitRateRef,
    손익분기점: breakEvenRef,
  };

  const handleClickTab = (id: tabId) => {
    setActiveTab(id);
    const target = tabToRef[id].current;
    const scrollRoot = scrollContainerRef?.current || window; // ref가 있으면 그 안에서, 없으면 window

    if (!target) return;

    if (scrollRoot instanceof Window) {
      const top =
        target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET; // 56 = 상단 탭/헤더 높이 보정
      window.scrollTo({ top, behavior: "smooth" });
    } else {
      const rootTop = scrollRoot.getBoundingClientRect().top;
      const targetTop = target.getBoundingClientRect().top;
      const offset = targetTop - rootTop - HEADER_OFFSET; // 스크롤 컨테이너 기준 오프셋
      scrollRoot.scrollTo({
        top: scrollRoot.scrollTop + offset,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const rootEl = scrollContainerRef?.current || null;
    const scrollTarget: HTMLElement | Window = rootEl || window;

    const handleScroll = () => {
      const sections: [tabId, HTMLDivElement | null][] = [
        ["영농 수익 구조", revenueStructureRef.current],
        ["농장 경쟁력", farmCompetitivenessRef.current],
        ["매출", salesRef.current],
        ["비용", costRef.current],
        ["수익률", profitRateRef.current],
        ["손익분기점", breakEvenRef.current],
      ];

      let currentId: tabId = "영농 수익 구조";
      let currentDistance = -Infinity;

      const rootTop = rootEl ? rootEl.getBoundingClientRect().top : 0;

      sections.forEach(([id, el]) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();

        // 섹션 top 이 "루트 상단 + 헤더 높이"에서 얼마나 떨어져 있는지
        const distance = rect.top - rootTop - HEADER_OFFSET;

        // distance <= 0 이면 헤더 아래를 이미 지나간 섹션
        // 그 중에서 가장 0에 가까운(최근에 지나간) 섹션을 active 로
        if (distance <= 0 && distance > currentDistance) {
          currentDistance = distance;
          currentId = id;
        }
      });

      setActiveTab(currentId);
    };

    // 처음 렌더될 때도 한 번 계산
    handleScroll();

    // 스크롤 타겟에 이벤트 등록
    scrollTarget.addEventListener("scroll", handleScroll);
    return () => {
      scrollTarget.removeEventListener("scroll", handleScroll);
    };
  }, [scrollContainerRef]);

  return (
    <s.Container>
      {!hideInnerTabs && (
        <s.TabContainer>
          {tabList.map((t) => (
            <s.Tab
              key={t}
              $active={activeTab === t}
              onClick={() => handleClickTab(t)}
            >
              {t}
            </s.Tab>
          ))}
        </s.TabContainer>
      )}

      <s.Section ref={revenueStructureRef}>
        <s.SectionTitle>영농 수익 구조</s.SectionTitle>
        <RevenueSection>
          <CommualContainer>
            <div className="label">영농대행(공동영농) 포함</div>
            <ToggleWrapper
              onClick={() => setIsTotal((prev) => !prev)}
              $checked={isTotal}
            >
              <ToggleCircle $checked={isTotal} />
            </ToggleWrapper>
          </CommualContainer>
          <RevenueContainer>
            <RevenueCard>
              <div className="label">경작기간 매출</div>
              <div className="value">
                {formatWonToEokMan(
                  isTotal
                    ? data.summary.totalSalesWithFarmAgent
                    : data.summary.totalSales
                )}
              </div>
            </RevenueCard>
            <RevenueCard>
              <div className="label">영업이익</div>
              <div className="value">
                {formatWonToEokMan(
                  isTotal
                    ? data.summary.totalIncomeWithFarmAgent
                    : data.summary.totalIncome
                )}
              </div>
            </RevenueCard>
            <RevenueCard>
              <div className="label">생산비</div>
              <div className="value">
                {formatWonToEokMan(
                  isTotal
                    ? data.summary.totalProductionCostWithFarmAgent
                    : data.summary.totalProductionCost
                )}
              </div>
            </RevenueCard>
            <RevenueCard>
              <div className="label">생산량</div>
              <div className="value">
                {isTotal
                  ? data.summary.totalQuantity
                  : data.summary.totalQuantity}
                kg
              </div>
            </RevenueCard>
            <RevenueCard>
              <div className="label">투자 수익률(ROI)</div>
              <div className="value">
                {isTotal ? data.summary.roiWithFarmAgent : data.summary.roi}%
              </div>
              <div className="desc">트랙터/부속기 비용 기준</div>
            </RevenueCard>
            <RevenueCard>
              <div className="label">손익분기점(BEP)</div>
              <div className="value">
                {isTotal
                  ? (data.summary.bepWithFarmAgent ?? 0)
                  : (data.summary.bep ?? 0)}
                년
              </div>
              <div className="desc">트랙터/부속기 비용 기준</div>
            </RevenueCard>
          </RevenueContainer>
        </RevenueSection>
      </s.Section>

      <s.Section ref={salesRef}>
        <s.SectionTitle>매출</s.SectionTitle>
        <SalesList>
          <SalesItem>
            <div className="label">경작기간 생산량</div>
            <div className="value">{data.farmComparison[1].quantity}kg</div>
          </SalesItem>
          <SalesItem>
            <div className="label">판매단가</div>
            <div className="value">
              {formatPriceNumber(data.farmComparison[1].unitPrice)}원
            </div>
          </SalesItem>
          <SalesItem>
            <div className="label strong">경작기간 매출</div>
            <div className="value strong">
              {formatWonToEokMan(data.farmComparison[1].totalSales)}
            </div>
          </SalesItem>
        </SalesList>
      </s.Section>

      <s.Section ref={costRef}>
        <s.SectionTitle>비용</s.SectionTitle>
        <Cost data={data.farmComparison} />
      </s.Section>

      <s.Section ref={profitRateRef}>
        <s.SectionTitle>
          수익률 (ROI)
          <UnitChangeWrapper ref={dropdownRef}>
            <UnitChangeButton onClick={() => setOpenRoiUnitChange(true)}>
              <span>기준</span> {roiUnit}
            </UnitChangeButton>
            {openRoiUnitChange && (
              <DropDown>
                {["총 투자", "총 비용"].map((item) => (
                  <DropdownItem
                    key={item}
                    onClick={() => {
                      setOpenRoiUnitChange(false);
                      setRoiUnit(item);
                    }}
                    $active={roiUnit === item}
                  >
                    <div className="label">{item}</div>
                    {item === roiUnit && (
                      <img src="/asset/images/check/Check24Green2.svg" alt="" />
                    )}
                  </DropdownItem>
                ))}
              </DropDown>
            )}
          </UnitChangeWrapper>
        </s.SectionTitle>
        {roiUnit === "총 투자" ? (
          <SalesList>
            <SalesItem>
              <div className="label strong">총 투자액</div>
              <div className="value strong">
                {formatWonToEokMan(data.roi.totalInvestment)}
              </div>
            </SalesItem>
            <SalesItem>
              <div className="label">트랙터 구입비</div>
              <div className="value">
                {formatWonToEokMan(data.roi.tractorPurchaseCost)}
              </div>
            </SalesItem>
            <SalesItem>
              <div className="label">부속기 구입비</div>
              <div className="value">
                {formatWonToEokMan(data.roi.implementPurchaseCost)}
              </div>
            </SalesItem>
            <SalesItem>
              <div className="label strong">경작기간 순이익</div>
              <div className="value strong">
                {
                  isTotal ?
                    formatWonToEokMan(data.roi.totalIncomeWithFarmAgent)
                    : formatWonToEokMan(data.roi.totalIncome)
                }
              </div>
            </SalesItem>
            <SalesItem>
              <div className="label strong">총 투자대비 수익률 (ROI)</div>
              <div className="value result">
                {
                  isTotal ? data.roi.roiWithFarmAgent ?? 0 : data.roi.roi ?? 0
                }%
              </div>
            </SalesItem>
          </SalesList>
        ) : (
          <SalesList>
            <SalesItem>
              <div className="label strong">연 비용</div>
              <div className="value strong">
                {formatWonToEokMan(data.roi.fixedCost + data.roi.variableCost)}
              </div>
            </SalesItem>
            <SalesItem>
              <div className="label">고정비</div>
              <div className="value">
                {formatWonToEokMan(data.roi.fixedCost)}
              </div>
            </SalesItem>
            <SalesItem>
              <div className="label">변동비</div>
              <div className="value">
                {formatWonToEokMan(data.roi.variableCost)}
              </div>
            </SalesItem>
            <SalesItem>
              <div className="label strong">경작기간 순이익</div>
              <div className="value strong">
                {
                  isTotal ?
                    formatWonToEokMan(data.roi.totalIncomeWithFarmAgent)
                    : formatWonToEokMan(data.roi.totalIncome)
                }
              </div>
            </SalesItem>
            <SalesItem>
              <div className="label strong">총 비용대비 수익률 (ROI)</div>
              <div className="value result">
                {data.roi.costRoi ?? 0}%
              </div>
            </SalesItem>
          </SalesList>
        )}
      </s.Section>

      <s.Section ref={breakEvenRef}>
        <s.SectionTitle>손익분기점 (BEP)</s.SectionTitle>
        <SalesList>
          <SalesItem>
            <div className="label strong">총 투자액</div>
            <div className="value strong">
              {formatWonToEokMan(data.bep.totalInvestment)}
            </div>
          </SalesItem>

          <SalesItem>
            <div className="label strong">경작기간 순이익</div>
            <div className="value strong">
              {
                isTotal ?
                  formatWonToEokMan(data.bep.totalIncomeWithFarmAgent)
                  : formatWonToEokMan(data.bep.totalIncome)
              }
            </div>
          </SalesItem>
          <SalesItem>
            <div className="label strong">BEP 년수</div>
            <div className="value result">
              {
                isTotal ? 
                  data.bep.bepWithFarmAgent ?? 0
                  : data.bep.bep ?? 0
              }년
            </div>
          </SalesItem>
        </SalesList>
      </s.Section>
    </s.Container>
  );
}

export default Economy;

const RevenueSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 8px;
`;

const CommualContainer = styled.div`
  display: flex;
  padding: 8px 0;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  & > .label {
    color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
    font-feature-settings:
      "liga" off,
      "clig" off;

    /* Mo/Body/L-16-Medium */
    font-family: Pretendard;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 150%; /* 24px */
  }
`;

const RevenueContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  align-self: stretch;
`;

const ToggleWrapper = styled.div<{ $checked: boolean }>`
  width: 56px;
  height: 28px;
  border-radius: 24px;
  background: ${({ $checked }) => ($checked ? "#1E9B6B" : "#C6D0DD")};
  display: flex;
  align-items: center;
  padding: 2px;
  cursor: pointer;
  transition: background 0.25s ease;
`;

const ToggleCircle = styled.div<{ $checked: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  transition: transform 0.25s ease;
  transform: ${({ $checked }) =>
    $checked ? "translateX(28px)" : "translateX(0)"};
`;

const RevenueCard = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  flex: 1 0 0;
  border-radius: 8px;
  background: #f7f8f9;

  & > .label {
    color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
    font-feature-settings:
      "liga" off,
      "clig" off;

    /* Mo/Body/S-14-Medium */
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
  }

  & > .value {
    color: var(--Alias-Color-Text-Dark-Text-Primary, #171e26);

    /* Web/Body/L-18-Bold */
    font-family: Pretendard;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 165%; /* 29.7px */
    letter-spacing: -0.18px;
  }

  & > .desc {
    color: var(--Alias-Color-Text-Dark-Text-Tertiary, rgba(23, 30, 38, 0.56));
    font-family: Pretendard;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px; /* 138.462% */
    letter-spacing: -0.26px;
  }
`;

const SalesList = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
`;

const SalesItem = styled.div`
  display: flex;
  padding: 8px 0;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */

  & > .label {
    color: var(--Alias-Color-Text-Dark-Text-Tertiary, rgba(23, 30, 38, 0.56));
  }

  & > .label.strong {
    color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  }

  & > .value {
    color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));
  }

  & > .value.strong {
    font-weight: 700;
  }

  & > .value.result {
    color: var(--Alias-Color-Button-cta-light-background-Primary, #1e9b6b);
    font-weight: 700;
  }
`;

const UnitChangeWrapper = styled.div`
  position: relative;
`;

const UnitChangeButton = styled.button`
  border: none;
  outline: none;
  cursor: pointer;

  display: flex;
  padding: 4px 12px;
  align-items: center;
  gap: 2px;
  border-radius: 1699px;
  background: var(--Alias-Color-Text-White-Gray-LightGray-200, #f0f0f0);

  color: var(--Alias-Color-Text-Dark-Text-Secondary, rgba(23, 30, 38, 0.78));

  /* Web/Caption/L-14-Medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */

  & > span {
    color: var(--Alias-Color-Text-Dark-Text-Tertiary, rgba(23, 30, 38, 0.56));

    /* Web/Caption/L-14-Medium */
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
  }
`;

const DropDown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;

  display: flex;
  width: 160px;
  padding: 4px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 16px;
  border: 1px solid var(--Alias-Color-Line-Outline, rgba(23, 30, 38, 0.1));
  background: var(--Alias-Color-Background-Seciton, #fff);

  /* shadow/light/+Y/02 */
  box-shadow:
    0 4px 12px 0 rgba(39, 44, 54, 0.05),
    0 2px 4px 0 rgba(39, 44, 54, 0.12),
    0 8px 32px 0 rgba(39, 44, 54, 0.08);
`;

const DropdownItem = styled.div<{ $active?: boolean }>`
  cursor: pointer;
  display: flex;
  padding: 8px 12px;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;

  & > .label {
    flex: 1 0 0;
    color: ${({ $active }) => ($active ? "#171E26" : "rgba(23, 30, 38, 0.56)")};
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
  }
`;
